import { CartService } from '../../../../core/services/cart/cart.service';
import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CommonModule, registerLocaleData } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Resident } from '../../../models/resident.model';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CartModalComponent } from '../../cart-modal/cart-modal.component';
import { ProfileAvatarComponent } from '../../profile-avatar/profile-avatar.component';
import { DepartmentWithResidents } from '../../../models/department.model';
import * as de from '@angular/common/locales/de';
import { NavigationService } from '../../../../core/services/utils/navigation.service';
import { PartnerPaymentGuardService } from '../../../../core/services/partner-payment-guard/partner-payment-guard.service';
import { MonthlyBudgetResetType, ShopType } from '../../../../core/types/enums';
import { SepaCheckComponent } from '../../sepa-check/sepa-check.component';
import { NoDeparmentOrOrderComponent } from '../../no-deparment-or-order/no-deparment-or-order.component';
import { InactiveResidentAlertComponent } from '../../inactive-resident-alert/inactive-resident-alert.component';
import { CustomersService } from '../../../../core/services/customers';
import { firstValueFrom, startWith } from 'rxjs';
import { NoConnextAccountIdErrorComponent } from '../../no-connext-account-id-error/no-connext-account-id-error.component';
import { ResidentsService } from '../../../../core/services/residents';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ResidentBudgetComponent } from '../../resident-budget/resident-budget.component';
import { ListOfNeedsDialogComponent } from '../../list-of-needs-dialog/list-of-needs-dialog.component';
import { ResidentNoteComponent } from '../../resident-note/resident-note.component';
import { Customer } from '../../../models/customer.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AnalyticsService } from '../../../../core/services/analytics/analytics.service';
import { HasNoValidPaymentMethodPipe } from '../../../pipes/has-no-valid-payment-method.pipe';
import { SHOP_LISTING_QUERY_PARAMS_RESET } from '../../../utils/shop-query-params';

@Component({
  selector: 'app-resident-sub-header',
  templateUrl: './resident-sub-header.component.html',
  styleUrls: ['./resident-sub-header.component.scss'],
  imports: [
    HasNoValidPaymentMethodPipe,
    MatIconModule,
    MatSlideToggleModule,
    CommonModule,
    TranslatePipe,
    ProfileAvatarComponent,
    SepaCheckComponent,
    NoDeparmentOrOrderComponent,
    InactiveResidentAlertComponent,
    NoConnextAccountIdErrorComponent,
    MatTooltipModule,
    ResidentBudgetComponent,
    ResidentNoteComponent,
    MatProgressSpinnerModule,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
  ],
})
export class ResidentSubHeaderComponent implements OnInit, OnChanges {
  isFocused = false;
  Resident = Resident;
  @Input() selectedResident: Resident | null = null;
  @Input() selectedDepartment: DepartmentWithResidents | null = null;
  @Input() customer: Customer | null = null;
  @Output() residentChanged = new EventEmitter<Resident>();
  firstResidentIndex = 0;
  activeShopPage: ShopType;
  ShopPage = ShopType;
  preventShoping: boolean = false;
  residentTotalValue = 0;
  residentItemsCountValue = 0;

  get selectedResidentImage() {
    if (this.selectedDepartment?.isB2B) {
      return this.selectedDepartment?.iconUrl;
    }
    return this.selectedResident?.image;
  }

  get lastResidentIndex() {
    return this.selectedDepartment.people.length - 1;
  }

  get isNextArrowHidden() {
    return this.getCurrentResidentIndex() === this.lastResidentIndex;
  }

  get isPreviousArrowHidden() {
    return this.getCurrentResidentIndex() === this.firstResidentIndex;
  }

  get isFavouritesActive() {
    return this.activeShopPage === this.ShopPage.Favourites;
  }
  get isLastOrderedActive() {
    return this.activeShopPage === this.ShopPage.LastOrdered;
  }

  get shouldBeWashy() {
    return (
      this.selectedResident?.active === 0 ||
      this.partnerPaymentGuardService.hasNoValidPaymentMethod(
        this.selectedResident
      )
    );
  }

  constructor(
    public cartService: CartService,
    public customersService: CustomersService,
    public residentsService: ResidentsService,
    private router: Router,
    private dialog: MatDialog,
    private navigationService: NavigationService,
    private analyticsService: AnalyticsService,
    private partnerPaymentGuardService: PartnerPaymentGuardService,
    private destroyRef: DestroyRef
  ) {
    registerLocaleData(de.default);
  }

  connextError(resident: Resident) {
    return (
      this.selectedDepartment.partnerDepartmentId &&
      !resident?.partnerAccountId &&
      resident?.hasIBAN === 0
    );
  }

  ngOnInit(): void {
    this.router.events
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.activeShopPage = activeShopPageFor(this.router.url);
      });

    this.cartService.getPreventShoping().subscribe(prevent => {
      this.preventShoping = prevent;
    });

    this.cartService.getAllCarts().subscribe(() => {
      this.updateCartInfo();
    });
  }

  ngOnChanges(): void {
    this.updateCartInfo();
  }

  getCurrentResidentIndex() {
    return this.selectedDepartment.people.findIndex(
      r => r.id === this.selectedResident.id
    );
  }

  updateCartInfo() {
    if (!this.selectedResident) {
      this.residentTotalValue = 0;
      this.residentItemsCountValue = 0;
      return;
    }

    const cart = this.cartService?.cart?.getResidentCart(
      this.selectedResident.id
    );

    this.residentTotalValue = cart?.total || 0;
    this.residentItemsCountValue = cart?.getTotalItemsCount || 0;
  }

  navigateToFavourites() {
    this.analyticsService.trackEvent('select_content', {
      content_type: 'favorites',
    });
    if (this.isFavouritesActive) {
      return this.navigateToMainShop();
    }

    if (this.router.url.includes('/shop/category')) {
      this.navigationService.setPreviousUrl(this.router.url);
    }

    this.router.navigate(['/shop/favourites'], {
      queryParams: SHOP_LISTING_QUERY_PARAMS_RESET,
      queryParamsHandling: 'merge',
    });
  }

  navigateToLastOrdered() {
    this.analyticsService.trackEvent('select_content', {
      content_type: 'recently_ordered',
    });
    if (this.isLastOrderedActive) {
      return this.navigateToMainShop();
    }

    if (this.router.url.includes('/shop/category')) {
      this.navigationService.setPreviousUrl(this.router.url);
    }
    this.router.navigate(['/shop/last-ordered'], {
      queryParams: SHOP_LISTING_QUERY_PARAMS_RESET,
      queryParamsHandling: 'merge',
    });
  }

  openBedarfslisteDialog(): void {
    this.analyticsService.trackEvent('select_content', {
      content_type: 'demand_list_create',
    });
    this.dialog.open(ListOfNeedsDialogComponent, {
      width: '80vw',
      autoFocus: false,
      disableClose: true,
    });
  }

  openDialog(): void {
    this.analyticsService.trackEvent('view_cart');
    this.dialog.open(CartModalComponent, {
      width: '1000px',
      autoFocus: false,
      maxHeight: '90vh',
    });
  }

  moveToResident(direction: 'next' | 'previous') {
    const selectedDepartment = this.selectedDepartment;
    const currentResidentIndex = this.getCurrentResidentIndex();
    let nextResidentIndex = currentResidentIndex;
    if (
      direction === 'next' &&
      currentResidentIndex !== this.lastResidentIndex
    ) {
      nextResidentIndex = currentResidentIndex + 1;
    } else if (
      direction === 'previous' &&
      currentResidentIndex !== this.firstResidentIndex
    ) {
      nextResidentIndex = currentResidentIndex - 1;
    }
    if (currentResidentIndex !== nextResidentIndex) {
      const newSelectedResident = selectedDepartment.people[nextResidentIndex];
      this.selectedResident = newSelectedResident;
      this.residentChanged.emit(newSelectedResident);
    }
  }

  async navigateToOrderOverview(): Promise<void> {
    this.router.navigate(['/shop/overview'], {
      queryParams: SHOP_LISTING_QUERY_PARAMS_RESET,
      queryParamsHandling: 'merge',
    });
  }

  navigateToMainShop() {
    this.router.navigate(['/shop'], {
      queryParams: SHOP_LISTING_QUERY_PARAMS_RESET,
      queryParamsHandling: 'merge',
    });
  }
}

const ACTIVE_SHOP_PAGES: Record<string, ShopType> = {
  '/shop/favourites': ShopType.Favourites,
  '/shop/last-ordered': ShopType.LastOrdered,
};

function activeShopPageFor(url: string): ShopType | null {
  return ACTIVE_SHOP_PAGES[url.split('?')[0]] ?? null;
}
